import React, { useState } from "react";
import {
  Box,
  CardMedia,
  Typography,
  Paper,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Product } from "../../types/product";
import { SiteInfoGrid } from "./ReviewSiteInfoGrid";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import { Checkbox } from "@mui/material"; 
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse, ButtonBase } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type Props = {
  item: Product;
  index: number;
  selected?: boolean; 
  onSelectChange?: (id: string, checked: boolean) => void;
};
export interface SiteForGrid {
  name: string;
  price: number | null;
  rating: number | null;
  count: number | null;
  url: string | null;
}

const ReviewCard = ({ item, index, selected, onSelectChange }: Props) => {
  // デフォルト値
  const safeScore = item.ai_score ?? 0;
  const safeSummary = item.ai_summary ?? "(AIレビュー要約は準備中です)";
  const [showDetails, setShowDetails] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openSummary, setOpenSummary] = useState(false);
  const [openProsCons, setOpenProsCons] = useState(false);


  /** review → SiteInfoGrid 用に SiteForGrid[] へ変換 */

  const sites: SiteForGrid[] = [];
  if (item.review.yahoo) {
    sites.push({
      name: "Yahoo!",
      price: item.review.yahoo.price ?? 0,
      rating: item.review.yahoo.rating ?? 0,
      count: item.review.yahoo.count ?? 0,
      url: item.review.yahoo.url ?? "",
    });
  }

  if (item.review.rakuten) {
    sites.push({
      name: "Rakuten",
      price: item.review.rakuten.price ?? 0,
      rating: item.review.rakuten.rating ?? 0,
      count: item.review.rakuten.count ?? 0,
      url: item.review.rakuten.url ?? "",
    });
  }

  if (item.review.amazon) {
    sites.push({
      name: "Amazon",
      price: item.review.amazon.price ?? 0,
      rating: item.review.amazon.rating ?? 0,
      count: item.review.amazon.count ?? 0,
      url: item.review.amazon.url ?? "",
    });
  }

  const clamp2 = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: 2,
    overflow: "hidden",
  };

  const clamp3 = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: 3,
    overflow: "hidden",
  };

  if (isMobile) {
    const mobileAccordionBtnSx = {
      width: "100%",
      px: 1.5,
      py: 0.6,
      borderRadius: 2,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
      "&:active": { opacity: 0.92 },
    } as const;

    const mobileAccordionIconSx = { color: "#fff" } as const;

    const mobileAccordionPanelSx = {
      mt: 1,
      p: 1.2,
      borderRadius: 2,
      backgroundColor: "#fff",
      border: "1px solid #eee",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    } as const;

    return (
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          border: "1px solid #eee",
          boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* 左：画像 + 情報 */}
          <Box sx={{ width: 112, flexShrink: 0 }}>
            <Box sx={{ position: "relative" }}>
              <Checkbox
                checked={selected}
                onChange={(e) => onSelectChange?.(item.id, e.target.checked)}
                sx={{ position: "absolute", top: -10, left: -10, zIndex: 5 }}
              />
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #eee",
                  backgroundColor: "#fafafa",
                }}
              >
                <CardMedia
                  component="img"
                  image={item.image ?? undefined}
                  alt="商品画像"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
            </Box>

            {/* AIスコア（スマホは左に大きく） */}
            <Box sx={{ mt: 1 }}>
              {/* 上段：AIスコア */}
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  AI score
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.6rem",
                    fontWeight: 900,
                    color: theme.palette.primary.main,
                    lineHeight: 1,
                  }}
                >
                  {safeScore.toFixed(1)}
                </Typography>
              </Box>

              {/* 下段：極細バー */}
              <Box
                sx={{
                  mt: 0.5,
                  height: 4,
                  width: "100%",
                  borderRadius: 2,
                  backgroundColor: "rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${safeScore}%`,
                    height: "100%",
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Box>
            </Box>
          </Box>
          {/* 右：タイトル / 要約 */}
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            {/* タイトル */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  ...clamp2,
                  lineHeight: 1.3,
                }}
              >
                {item.title}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.5 }}>
              <SiteInfoGrid sites={sites} dense />
            </Box>

            {/* ===== AIレビュー要約（スマホ専用） ===== */}
            <Box sx={{ mt: 1 }}>
              <ButtonBase
                onClick={() => setOpenSummary((v) => !v)}
                sx={mobileAccordionBtnSx}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                  AIレビュー要約
                </Typography>

                {openSummary ? (
                  <KeyboardArrowUpIcon sx={mobileAccordionIconSx} />
                ) : (
                  <KeyboardArrowDownIcon sx={mobileAccordionIconSx} />
                )}
              </ButtonBase>

              <Collapse in={openSummary} timeout="auto" unmountOnExit>
                <Box sx={mobileAccordionPanelSx}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                    {safeSummary}
                  </Typography>
                </Box>
              </Collapse>
            </Box>

            {/* ▼▼ スマホ：良い点・悪い点（独立） ▼▼ */}
            <Box sx={{ mt: 1 }}>
              <ButtonBase
                onClick={() => setOpenProsCons((v) => !v)}
                sx={mobileAccordionBtnSx}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <ThumbUpAltOutlinedIcon fontSize="small" />
                  良い点・悪い点
                </Typography>

                {openProsCons ? (
                  <KeyboardArrowUpIcon sx={mobileAccordionIconSx} />
                ) : (
                  <KeyboardArrowDownIcon sx={mobileAccordionIconSx} />
                )}
              </ButtonBase>

              <Collapse in={openProsCons} timeout="auto" unmountOnExit>
                <Box sx={{ ...mobileAccordionPanelSx, px: 1.2, pb: 1.2 }}>
                  {/* 中身はそのまま */}
                  {item.ai_pros?.length ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                        👍 良い点
                      </Typography>
                      {item.ai_pros.slice(0, 3).map((pro, idx) => (
                        <Typography key={idx} variant="body2" sx={{ pl: 1, mb: 0.25 }}>
                          ・{pro}
                        </Typography>
                      ))}
                    </>
                  ) : null}

                  {item.ai_cons?.length ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1, mb: 0.5 }}>
                        👎 悪い点
                      </Typography>
                      {item.ai_cons.slice(0, 3).map((con, idx) => (
                        <Typography key={idx} variant="body2" sx={{ pl: 1, mb: 0.25 }}>
                          ・{con}
                        </Typography>
                      ))}
                    </>
                  ) : null}

                  {!item.ai_pros?.length && !item.ai_cons?.length && (
                    <Typography variant="body2" color="text.secondary">
                      まだ分析結果がありません。
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid #eee",
        boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* 左カラム */}
        <Box sx={{ position: "relative" }}>
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectChange?.(item.id, e.target.checked)}
            sx={{ position: "absolute", top: -8, left: -8, zIndex: 5 }}
          />
        </Box>
        <Box sx={{ flex: "0 0 30%", minWidth: 200, maxWidth: 360 }}>
          <Box sx={{ position: "relative" }}>
            <CardMedia
              component="img"
              image={item.image ?? undefined}
              alt="商品画像"
              sx={{
                width: "100%",
                height: 130,
                borderRadius: 2,
                objectFit: "contain",
                border: "1px solid #eee",
              }}
            />
          </Box>
          
          <SiteInfoGrid sites={sites} />
        </Box>

        {/* 右カラム */}
        {/* タイトル */}
        <Box sx={{ flex: "1 1 70%", minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            noWrap
            sx={{
              mt: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.title}
          </Typography>
          {/* AIスコア（横一列 & コンパクト） */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 0.7,
              border: "1px solid #eee",
              borderRadius: 2,
              background: "#fff",
              flex: "0 0 auto",
            }}
          >
            {/* 左：AIスコアラベル */}
            <Chip
              icon={<AutoAwesomeIcon fontSize="small" />}
              label="AIスコア"
              size="small"
              variant="outlined"
              sx={{
                "& .MuiChip-label": { fontSize: "0.75rem", px: 0.6 },
                backgroundColor: "rgba(37, 99, 235, 0.06)",
                height: 24,
              }}
            />

            {/* 中央：スコア数値 */}
            <Typography
              variant="body1"
              sx={{
                color: "#2563eb",
                fontSize: "1.35rem",
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {safeScore.toFixed(1)}
            </Typography>

            {/* 右：インフォアイコン（数値の右に移動） */}
            <Tooltip
              title="Aish5Xでは各ECサイトのレビューをAIが解析し、独自スコアを算出しています。"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{
                  color: "action.active",
                  verticalAlign: "middle",
                  ml: 0.2,
                  fontSize: "1.1rem",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Box>
          {/* AIレビュー要約 */}
          <Box
            sx={{
              p: 1.5,
              border: "1px solid #eee",
              borderRadius: 2,
              backgroundColor: "#fafafa",
              position: "relative",
              minWidth: 0,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                position: "absolute",
                top: 6,
                left: 12,
                background: "#fff",
                px: 0.5,
              }}
            >
              <AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5 }} />
              AIレビュー要約
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                whiteSpace: "pre-line",
                color: safeSummary ? "inherit" : "text.disabled",
              }}
            >
              {safeSummary}
            </Typography>
            {/* 良い点・悪い点を開閉するタブ */}
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label={
                  showDetails ? "良い点・悪い点を閉じる" : "良い点・悪い点を見る"
                }
                icon={showDetails ? <ExpandLess /> : <ExpandMore />}
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: "rgba(37, 99, 235, 0.10)",
                  color: "#2563eb",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "rgba(37, 99, 235, 0.20)",
                  },
                }}
              />
            </Box>

            {/* 良い点＋悪い点まとめて表示 */}
            {showDetails && (
              <Box sx={{ mt: 1.5 }}>
                {/* 良い点 */}
                {item.ai_pros && item.ai_pros.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 0.5 }}
                    >
                      👍 良い点
                    </Typography>
                    {item.ai_pros.map((pro, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{ pl: 1.5, mb: 0.5 }}
                      >
                        ・{pro}
                      </Typography>
                    ))}
                  </>
                )}

                {/* 悪い点 */}
                {item.ai_cons && item.ai_cons.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mt: 1.5, mb: 0.5 }}
                    >
                      👎 悪い点
                    </Typography>
                    {item.ai_cons.map((con, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{ pl: 1.5, mb: 0.5 }}
                      >
                        ・{con}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(ReviewCard);